import { useEffect, useState } from 'react';
import { fuelAPI, fuelCardAPI, vehicleAPI, driverAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Modal, Input, Select, Loading, Alert } from '../components/ui';
import { formatRs } from '../utils/currency';

const today = () => new Date().toISOString().split('T')[0];

const emptyCard = {
  vehicle_id:'', driver_id:'', card_no:'', issue_mileage_km:'', issue_date: today(),
  monthly_limit_l:'', fuel_type:'diesel', issued_by:'',
};
const emptyLog = {
  fuel_card_id:'', vehicle_id:'', driver_id:'', fuel_type:'diesel',
  quantity_l:'', cost_per_l_rs:'', odometer:'', mileage_km:'',
  station:'', issue_date: today(), notes:'', received_by:'', fuel_card_no:'',
};

export default function Fuel() {
  const { can, user } = useAuth();
  const [tab, setTab] = useState('cards');

  const [cards,    setCards]    = useState([]);
  const [logs,     setLogs]     = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers,  setDrivers]  = useState([]);
  const [officers, setOfficers] = useState([]);
  const [stats,    setStats]    = useState(null);
  const [month,    setMonth]    = useState('');
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [saving,   setSaving]   = useState(false);

  const [cardModal, setCardModal] = useState(false);
  const [editCard,  setEditCard]  = useState(null);
  const [cardForm,  setCardForm]  = useState(emptyCard);

  const [logModal, setLogModal] = useState(false);
  const [logForm,  setLogForm]  = useState(emptyLog);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([
      fuelCardAPI.getAll().catch(() => ({ data: { data: [] } })),
      fuelAPI.getAll({ limit: 100 }),
      vehicleAPI.getAll({ limit: 200 }),
      driverAPI.getAll({ limit: 200 }),
      userAPI.getAll().catch(() => ({ data: { data: [] } })),
    ])
      .then(([c, f, v, d, u]) => {
        setCards(c.data.data || []);
        setLogs(f.data.data || []);
        setVehicles(v.data.data || []);
        setDrivers(d.data.data || []);
        setOfficers((u.data.data || []).filter(x => ['admin','transport_officer','manager'].includes(x.role)));
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const loadStats = (m) => {
    fuelAPI.getStats(m ? { month: m } : {})
      .then(r => setStats(r.data.data))
      .catch(() => setError('Failed to load consumption report'));
  };
  useEffect(() => { if (tab === 'report') loadStats(month); }, [tab, month]);

  // ── Cards ──────────────────────────────────────────────────────────────────
  const openCardAdd = () => { setEditCard(null); setCardForm({ ...emptyCard, issue_date: today(), issued_by: user?.id || '' }); setCardModal(true); };
  const openCardEdit = (c) => {
    setEditCard(c);
    setCardForm({
      vehicle_id: c.vehicle_id, driver_id: c.driver_id || '', card_no: c.card_no,
      issue_mileage_km: c.issue_mileage_km || '', issue_date: c.issue_date || today(),
      monthly_limit_l: c.monthly_limit_l || '', fuel_type: c.fuel_type, issued_by: c.issued_by || '',
    });
    setCardModal(true);
  };
  const onCardVehicle = (e) => {
    const id = e.target.value;
    const v = vehicles.find(x => String(x.id) === id);
    setCardForm({ ...cardForm, vehicle_id: id, issue_mileage_km: v?.odometer ?? cardForm.issue_mileage_km });
  };
  const saveCard = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editCard) { await fuelCardAPI.update(editCard.id, cardForm); flash('Fuel card updated'); }
      else          { await fuelCardAPI.create(cardForm); flash('Fuel card issued'); }
      setCardModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save fuel card'); }
    finally { setSaving(false); }
  };
  const toggleCard = async (c) => {
    try { await fuelCardAPI.update(c.id, { status: c.status === 'active' ? 'inactive' : 'active' }); load(); }
    catch (err) { setError(err.response?.data?.message || 'Failed'); }
  };

  // ── Logs ───────────────────────────────────────────────────────────────────
  const openLogAdd = () => { setLogForm({ ...emptyLog, issue_date: today() }); setLogModal(true); };
  const onLogCard = (e) => {
    const id = e.target.value;
    const c = cards.find(x => String(x.id) === id);
    if (c) {
      setLogForm({
        ...logForm, fuel_card_id: id,
        vehicle_id: c.vehicle_id, driver_id: c.driver_id || '',
        fuel_type: c.fuel_type, fuel_card_no: c.card_no,
        odometer: c.vehicle?.odometer ?? logForm.odometer,
      });
    } else {
      setLogForm({ ...logForm, fuel_card_id: '', fuel_card_no: '' });
    }
  };
  const saveLog = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await fuelAPI.create(logForm);
      flash('Fuel log saved'); setLogModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save fuel log'); }
    finally { setSaving(false); }
  };

  const activeCards = cards.filter(c => c.status === 'active');
  const canEdit = can('admin','transport_officer','manager');

  const UsageBar = ({ used, limit }) => {
    if (!limit) return <span className="text-xs text-gray-400">{used} L</span>;
    const pct = Math.min(100, Math.round((used / limit) * 100));
    const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-400' : 'bg-green-500';
    return (
      <div className="w-32">
        <div className="flex justify-between text-xs mb-0.5">
          <span>{used} / {Number(limit)} L</span><span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-gray-200 rounded"><div className={`h-1.5 rounded ${color}`} style={{ width: `${pct}%` }} /></div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Fuel Management"
        subtitle={`${activeCards.length} active card${activeCards.length === 1 ? '' : 's'} · ${logs.length} recent logs`}
        action={canEdit && (tab === 'cards'
          ? <Button onClick={openCardAdd}>+ Issue Fuel Card</Button>
          : tab === 'logs' ? <Button onClick={openLogAdd}>+ Log Fuel</Button> : null)} />

      {error   && <Alert type="error"   message={error}   />}
      {success && <Alert type="success" message={success} />}

      <div className="flex gap-2">
        {[['cards','💳 Fuel Cards'],['logs','⛽ Fuel Logs'],['report','📊 Consumption Report']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <Loading /> : tab === 'cards' ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>{['Card No','Vehicle','Driver','Fuel','Monthly Limit / Used','Issued','Issue Mileage','Status','Actions'].map(h =>
                <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {cards.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No fuel cards yet — issue the first one</td></tr>}
              {cards.map(c => (
                <tr key={c.id} className={`hover:bg-gray-50 ${c.status === 'inactive' ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-3 font-mono text-xs font-semibold">{c.card_no}</td>
                  <td className="px-3 py-3 font-mono text-primary-600 font-semibold text-xs">{c.vehicle?.reg_number}</td>
                  <td className="px-3 py-3 text-xs">{c.driver?.user?.name || '—'}</td>
                  <td className="px-3 py-3 text-xs capitalize">{c.fuel_type}</td>
                  <td className="px-3 py-3"><UsageBar used={c.month_used_l} limit={c.monthly_limit_l} /></td>
                  <td className="px-3 py-3 text-xs">{c.issue_date || '—'}<p className="text-gray-400">{c.issuer?.name}</p></td>
                  <td className="px-3 py-3 text-xs">{c.issue_mileage_km ? `${c.issue_mileage_km} km` : '—'}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
                  </td>
                  <td className="px-3 py-3">
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" title="Edit" onClick={() => openCardEdit(c)}>✏️</Button>
                        <Button variant="ghost" size="sm" title={c.status === 'active' ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleCard(c)}>{c.status === 'active' ? '🚫' : '✅'}</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === 'logs' ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>{['Date','Vehicle','Driver','Card No.','Fuel','Qty (L)','Rate (Rs.)','Total (Rs.)','Odometer','Station','Received By','Notes'].map(h =>
                <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {logs.length === 0 && <tr><td colSpan={12} className="px-4 py-10 text-center text-gray-400">No fuel logs yet</td></tr>}
              {logs.map(f => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{f.issue_date}</td>
                  <td className="px-3 py-3 font-mono text-primary-600 font-semibold text-xs whitespace-nowrap">{f.vehicle?.reg_number}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{f.driver?.user?.name || '—'}</td>
                  <td className="px-3 py-3 text-xs font-mono text-gray-600">{f.fuel_card_no || '—'}</td>
                  <td className="px-3 py-3 text-xs capitalize">{f.fuel_type}</td>
                  <td className="px-3 py-3 text-xs font-medium">{f.quantity_l} L</td>
                  <td className="px-3 py-3 text-xs">{f.cost_per_l_rs ? formatRs(f.cost_per_l_rs) : '—'}</td>
                  <td className="px-3 py-3 text-xs font-semibold text-gray-800 whitespace-nowrap">{f.total_cost_rs ? formatRs(f.total_cost_rs) : '—'}</td>
                  <td className="px-3 py-3 text-xs">{f.odometer ? `${f.odometer} km` : '—'}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{f.station || '—'}</td>
                  <td className="px-3 py-3 text-xs">{f.received_by_user?.name || '—'}</td>
                  <td className="px-3 py-3 text-xs max-w-[150px] truncate" title={f.notes}>{f.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-gray-600">Period:</label>
            <input type="month" className="border rounded-lg px-3 py-2 text-sm"
              value={month} onChange={e => setMonth(e.target.value)} />
            {month && <Button variant="ghost" size="sm" onClick={() => setMonth('')}>All time</Button>}
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>{['Vehicle','Fills','Total Litres','Total Cost','Km Travelled','Avg Consumption','Cost / Km'].map(h =>
                  <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {!stats?.consumption?.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No fuel data {month ? `for ${month}` : 'yet'}</td></tr>}
                {(stats?.consumption || []).map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <span className="font-mono text-primary-600 font-semibold text-xs">{r.reg_number}</span>
                      <p className="text-xs text-gray-400">{r.brand} {r.model}</p>
                    </td>
                    <td className="px-3 py-3 text-xs">{r.fills}</td>
                    <td className="px-3 py-3 text-xs font-medium">{Number(r.total_litres).toFixed(1)} L</td>
                    <td className="px-3 py-3 text-xs font-semibold">{formatRs(r.total_cost_rs)}</td>
                    <td className="px-3 py-3 text-xs">{Number(r.km_travelled) > 0 ? `${Number(r.km_travelled).toLocaleString()} km` : '—'}</td>
                    <td className="px-3 py-3">
                      {r.km_per_l
                        ? <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.km_per_l >= 10 ? 'bg-green-100 text-green-700' : r.km_per_l >= 6 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700'}`}>{r.km_per_l} km/L</span>
                        : <span className="text-xs text-gray-400" title="Needs mileage or odometer readings on the logs">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs">{r.cost_per_km ? `${formatRs(r.cost_per_km)}/km` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400">
            Avg consumption uses the mileage entered on each log (or odometer range when mileage isn't recorded) ÷ total litres.
          </p>
        </div>
      )}

      {/* ══ ISSUE / EDIT FUEL CARD (part 1 — master details) ═══════════════════ */}
      <Modal open={cardModal} onClose={() => setCardModal(false)}
        title={editCard ? `Edit Fuel Card ${editCard.card_no}` : 'Issue Fuel Card'} size="lg">
        <form onSubmit={saveCard} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Select label="Vehicle *" value={cardForm.vehicle_id} onChange={onCardVehicle} required disabled={!!editCard}>
              <option value="">Select vehicle…</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number} — {v.brand} {v.model}</option>)}
            </Select>
            <Select label="Driver" value={cardForm.driver_id}
              onChange={e => setCardForm({ ...cardForm, driver_id: e.target.value })}>
              <option value="">Select driver…</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.user?.name}</option>)}
            </Select>
            <Input label="Fuel Card Number *" required value={cardForm.card_no}
              onChange={e => setCardForm({ ...cardForm, card_no: e.target.value })} placeholder="e.g. FC-2026-0042" />
            <Input label="Monthly Card Amount (Litres)" type="number" step="0.01"
              value={cardForm.monthly_limit_l}
              onChange={e => setCardForm({ ...cardForm, monthly_limit_l: e.target.value })} placeholder="e.g. 200" />
            <Input label="Current Mileage at Issue (km)" type="number"
              value={cardForm.issue_mileage_km}
              onChange={e => setCardForm({ ...cardForm, issue_mileage_km: e.target.value })} placeholder="auto-filled from vehicle" />
            <Input label="Issue Date *" type="date" required value={cardForm.issue_date}
              onChange={e => setCardForm({ ...cardForm, issue_date: e.target.value })} />
            <Select label="Main Fuel Type" value={cardForm.fuel_type}
              onChange={e => setCardForm({ ...cardForm, fuel_type: e.target.value })}>
              <option value="diesel">Diesel</option><option value="petrol">Petrol</option><option value="electric">Electric</option>
            </Select>
            <Select label="Issued By (Officer)" value={cardForm.issued_by}
              onChange={e => setCardForm({ ...cardForm, issued_by: e.target.value })}>
              <option value="">Select officer…</option>
              {officers.map(o => <option key={o.id} value={o.id}>{o.name} ({o.role})</option>)}
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCardModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editCard ? 'Save Changes' : 'Issue Card'}</Button>
          </div>
        </form>
      </Modal>

      {/* ══ LOG FUEL (part 2 — day-to-day) ═════════════════════════════════════ */}
      <Modal open={logModal} onClose={() => setLogModal(false)} title="Log Fuel" size="lg">
        <form onSubmit={saveLog} className="space-y-4">
          <Select label="Fuel Card" value={logForm.fuel_card_id} onChange={onLogCard}>
            <option value="">No card — cash / manual</option>
            {activeCards.map(c => (
              <option key={c.id} value={c.id}>{c.card_no} — {c.vehicle?.reg_number} ({c.driver?.user?.name || 'no driver'})</option>
            ))}
          </Select>

          {logForm.fuel_card_id ? (
            <p className="-mt-2 text-xs text-gray-500">
              Vehicle, driver and fuel type are taken from the card automatically.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <Select label="Vehicle *" value={logForm.vehicle_id}
                onChange={e => setLogForm({ ...logForm, vehicle_id: e.target.value })} required>
                <option value="">Select vehicle…</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number}</option>)}
              </Select>
              <Select label="Driver" value={logForm.driver_id}
                onChange={e => setLogForm({ ...logForm, driver_id: e.target.value })}>
                <option value="">Select driver…</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.user?.name}</option>)}
              </Select>
              <Select label="Fuel Type" value={logForm.fuel_type}
                onChange={e => setLogForm({ ...logForm, fuel_type: e.target.value })}>
                <option value="diesel">Diesel</option><option value="petrol">Petrol</option><option value="electric">Electric</option>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input label="Date *" type="date" required value={logForm.issue_date}
              onChange={e => setLogForm({ ...logForm, issue_date: e.target.value })} />
            <Input label="Quantity (Litres) *" type="number" step="0.01" required value={logForm.quantity_l}
              onChange={e => setLogForm({ ...logForm, quantity_l: e.target.value })} placeholder="e.g. 45.50" />
            <Input label="Rate per Litre (Rs.) *" type="number" step="0.01" required value={logForm.cost_per_l_rs}
              onChange={e => setLogForm({ ...logForm, cost_per_l_rs: e.target.value })} placeholder="e.g. 390.00" />
            <Input label="Current Odometer (km)" type="number" value={logForm.odometer}
              onChange={e => setLogForm({ ...logForm, odometer: e.target.value })} placeholder="e.g. 46200" />
            <Input label="Mileage since last fill (km)" type="number" step="0.1" value={logForm.mileage_km}
              onChange={e => setLogForm({ ...logForm, mileage_km: e.target.value })} placeholder="for consumption calc" />
            <Input label="Fuel Station" value={logForm.station}
              onChange={e => setLogForm({ ...logForm, station: e.target.value })} placeholder="e.g. CPC - Colombo 03" />
            <Select label="Received By (Officer)" value={logForm.received_by}
              onChange={e => setLogForm({ ...logForm, received_by: e.target.value })}>
              <option value="">Select officer…</option>
              {officers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            <Input label="Notes" value={logForm.notes}
              onChange={e => setLogForm({ ...logForm, notes: e.target.value })} />
          </div>

          {logForm.quantity_l && logForm.cost_per_l_rs && (
            <p className="text-sm text-gray-600">
              Total: <strong>{formatRs(Number(logForm.quantity_l) * Number(logForm.cost_per_l_rs))}</strong>
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setLogModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Log'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
